import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getEvent, getPost } from "../../api/api";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import Post from "./Post";
import Event from "./Event";

/**
 * Single post or event permalink page.
 * @param {Object} props
 * @param {number} props.userId
 * @param {object} props.user
 * @param {"post"|"event"} props.contentType
 * @returns {JSX.Element}
 */
export default function ContentDetailPage({ userId, user, contentType }) {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contentId = contentType === "event" ? params.eventId : params.postId;
  const focusCommentId = Number(searchParams.get("comment"));
  const resolvedFocusCommentId =
    Number.isFinite(focusCommentId) && focusCommentId > 0 ? focusCommentId : null;

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      setItem(null);

      const id = Number(contentId);
      if (!Number.isFinite(id) || id <= 0) {
        setError(contentType === "event" ? "Event not found" : "Post not found");
        setLoading(false);
        return;
      }

      try {
        const response =
          contentType === "event" ? await getEvent(id) : await getPost(id, userId);
        if (cancelled) return;
        if (response?.success && response.data) {
          setItem(response.data);
        } else {
          setError(
            response?.error ||
              response?.message ||
              (contentType === "event" ? "Event not found" : "Post not found")
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error(`Error loading ${contentType}:`, err);
        setError(
          contentType === "event" ? "Failed to load event" : "Failed to load post"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [contentId, contentType, userId]);

  return (
    <div className="overflow-x-hidden bg-ui-background pb-8">
      <div className="mx-auto max-w-3xl px-3 py-6 sm:px-4">
        {loading ? (
          <div className="rounded-lg border border-ui-border bg-ui-card p-6">
            <div className="mb-4 flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
            <Skeleton className="mb-3 h-4 w-full" />
            <Skeleton className="h-40 w-full rounded-md" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-lg border border-ui-border bg-ui-card p-8 text-center">
            <p className="font-display text-lg text-text-primary">
              {contentType === "event"
                ? "This event isn’t available"
                : "This post isn’t available"}
            </p>
            <p className="mt-2 text-sm text-error">{error}</p>
            <Button asChild className="mt-6">
              <Link to="/feed">See more posts</Link>
            </Button>
          </div>
        ) : null}

        {!loading && !error && item ? (
          <>
            {contentType === "event" ? (
              <Event
                userId={userId}
                user={user}
                event={item}
                hideContent={false}
                focusCommentId={resolvedFocusCommentId}
                onDeleted={() => navigate("/feed")}
              />
            ) : (
              <Post
                userId={userId}
                user={user}
                post={item}
                hideContent={false}
                focusCommentId={resolvedFocusCommentId}
                onDeleted={() => navigate("/feed")}
              />
            )}
            <div className="mt-2 flex justify-center">
              <Button asChild variant="secondary">
                <Link to="/feed">See more posts</Link>
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
